import React from 'react';
import { Button, Text, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SEHomeCard } from '../../../../common/components/SEHomeCard';
import { IS_NATIVE } from '../../../../common/constants';
import { resolveTrackingConsent } from '../../../../utils/common/tracking-privacy';
import { trackDynatraceConsentManagementClosed } from '../../../../utils/native/dynatrace-events';
import { selectUser } from '../../../user/userSlice';
import { AccountMenuButton } from '../../AccountMenuButton';
import { configureConsentManagementSettings } from '../consent-management';

export function ConsentManagementButton() {
  const toast = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [isOpeningConsentManager, setIsOpeningConsentManager] = React.useState(false);

  async function handleConsentManagementClick() {
    /* istanbul ignore if -- @preserve */
    // Prevent multiple simultaneous openings of the consent management screen.
    if (isOpeningConsentManager) return;

    if (IS_NATIVE) {
      setIsOpeningConsentManager(true);
      try {
        const consentPurposes = await configureConsentManagementSettings();
        await resolveTrackingConsent(
          undefined,
          undefined,
          consentPurposes,
          false,
          user.role,
          trackDynatraceConsentManagementClosed
        );
      } catch (e) {
        await resolveTrackingConsent(undefined, undefined, []);
        console.error(e);
        if ((e as AxiosError)?.response?.status === 400) {
          // The consent module requires a valid user access token via cIAM --
          // -- user needs to relog to access this module in the event of an auth error.
          toast({
            title: t('Account.log in required'),
            description: (
              <>
                {t('Account.error fetching consent')}
                <Button mt={2} onClick={() => navigate('/logout')} display="block" bgColor="white" color="black">
                  {t('Account.tap here to logout')}
                </Button>
              </>
            ),
            status: 'error',
            duration: 15000,
            isClosable: true,
          });
        } else {
          Sentry.captureException(e, {
            tags: {
              category: 'feature',
              feature: 'privacy',
              component: 'ConsentManagementButton',
              method: 'handleConsentManagementClick',
            },
          });
          toast({
            title: t('Account.error configuring consent'),
            description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
            status: 'error',
          });
        }
      } finally {
        setIsOpeningConsentManager(false);
      }
    } else {
      toast({
        title: t('Common.unavailable on web'),
        description: t('Common.download app to use feature'),
        status: 'error',
      });
    }
  }

  return (
    <SEHomeCard my={2} mb={5} borderTopLeftRadius="5px" p={0}>
      <AccountMenuButton
        data-testid="consent-management-button"
        onClick={handleConsentManagementClick}
        includeBottomDivider={false}
        isLoading={isOpeningConsentManager}
        isDisabled={isOpeningConsentManager}
      >
        <Text fontSize={16} fontWeight="normal">
          {t('Account.consent management')}
        </Text>
      </AccountMenuButton>
    </SEHomeCard>
  );
}
